<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="650px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template v-if="isNewPackage">
                        Dodaj pakowanie
                    </template>
                    <template v-else>
                        Edycja pakowania
                    </template>
                </h1>
            </v-card-title>

            <v-card-text>
                <v-row>
                    <v-col cols="12" sm="5">
                        <v-select
                            v-model="packageModel.boxType"
                            :items="boxTypesData"
                            label="Sposób pakowania"
                            item-text="name"
                            return-object
                            outlined
                            dense
                            :error-messages="packageBoxTypeErrors"
                            @change="changeBoxType"
                        />
                    </v-col>
                    <v-col cols="12" sm="7">
                        <v-autocomplete
                            v-model="packageModel.packageType"
                            label="Opakowanie"
                            :items="filteredPackageTypes"
                            item-text="name"
                            return-object
                            outlined
                            dense
                            :error-messages="packagePackageTypeErrors"
                            @change="changePackageType"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="auto">
                        <v-text-field
                            v-model="packageModel.packageAmount"
                            label="Ilość opakowań"
                            type="number"
                            :min="1"
                            outlined
                            dense
                            hide-spin-buttons
                            :error-messages="packageAmountErrors"
                        />
                    </v-col>
                </v-row>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Anuluj"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    :data-sentry-label="isNewPackage ? 'Dodaj' : 'Zapisz'"
                    @click="onSave"
                >
                    <template v-if="isNewPackage">
                        Dodaj
                    </template>
                    <template v-else>
                        Zapisz
                    </template>
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { isEmpty } from 'lodash'
import { required, minValue, integer } from 'vuelidate/lib/validators'

export default {
    name: 'PackageDialog',
    props: {
        isOpen: {
            type: Boolean
        },
        packageData: {
            type: Object,
            default: null
        },
        boxTypesData: {
            type: Array,
            default: null
        },
        packageTypesData: {
            type: Array,
            default: null
        },
        boxesInfo: {
            type: Array,
            default: null
        },
        parcelData: {
            type: Object,
            default: null
        },
    },
    data() {
        return {
            packageModel: {
                boxArrangement:{
                    x: null,
                    y: null,
                    z: null
                },
                collectingBox:false,
                packageAmount: null,
                packageType: null,
                totalWeight: null
            },
            filteredPackageTypes: [],
            boxesInfoSum: 0
        }
    },
    validations: {
        packageModel: {
            boxTypeId: {
                required
            },
            packageType: {
                required
            },
            packageAmount: {
                required,
                minValue: minValue(1),
                integer
            }
        }
    },
    computed: {
        isNewPackage() {
            return isEmpty(this.packageData)
        },
        // Walidacje
        packageBoxTypeErrors() {
            const errors = []

            if (!this.$v.packageModel.boxTypeId.$dirty) return errors

            if (!this.$v.packageModel.boxTypeId.required) {
                errors.push('Proszę podać sposób pakowania.')
            }

            return errors
        },
        packagePackageTypeErrors() {
            const errors = []

            if (!this.$v.packageModel.packageType.$dirty) return errors

            if (!this.$v.packageModel.packageType.required) {
                ('Proszę podać rodzaj opakowania.')
            }

            return errors
        },
        packageAmountErrors() {
            const errors = []

            if (!this.$v.packageModel.packageAmount.$dirty) return errors

            if (!this.$v.packageModel.packageAmount.required) {
                errors.push('Liczba opakowań jest wymagana')
            }

            if (!this.$v.packageModel.packageAmount.minValue || !this.$v.packageModel.packageAmount.integer) {
                errors.push('Proszę podać całkowitą dodatnią liczbę opakowań')
            }

            return errors
        },
    },
    watch: {
        isOpen(boolean) {
            if (boolean) {
                if (!isEmpty(this.packageData)) {
                    this.packageModel = JSON.parse(JSON.stringify(this.packageData))
                    this.boxesInfo.length > 0 ? this.boxesInfo.forEach(box => {box.index !== this.packageData.index ? this.boxesInfoSum += parseInt(box.packageAmount): 0
                    }) : 0
                } else {
                    this.boxesInfoSum = this.boxesInfo.length > 0 ? this.boxesInfo.reduce((acc, box) =>
                        acc + parseInt(box.packageAmount), 0) : 0
                }
                this.packageModel.totalWeight = JSON.parse(JSON.stringify(this.parcelData.totalWeight))
                this.filteredPackageTypes = this.packageTypesData.filter(item => item.boxType.id === this.packageModel.boxTypeId)
            } else {
                setTimeout(() => {
                    this.$v.$reset()
                    this.filteredPackageTypes = []
                    this.boxesInfoSum = 0
                    this.packageModel = {
                        boxArrangement:{
                            x: null,
                            y: null,
                            z: null
                        },
                        collectingBox:false,
                        packageAmount: null,
                        packageType: null,
                        totalWeight: null
                    }
                }, 500)
            }
        }
    },
    methods: {
        onSave() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            if (this.parcelData.numberOfPackages < 1) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Osiągnięto minimalną ilość kartonów.'
                })

                return
            }

            if (this.boxesInfoSum + parseInt(this.packageModel.packageAmount) > this.parcelData.numberOfPackages) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Osiągnięto maksymalną ilość kartonów.'
                })

                return
            }

            const packageModel = JSON.parse(JSON.stringify(this.packageModel))

            packageModel.packageAmount = parseInt(this.packageModel.packageAmount)
            this.$emit('on-save', packageModel, this.isNewPackage)
        },
        onClose() {
            this.$emit('on-close')
        },
        changeBoxType(boxType) {
            this.filteredPackageTypes = this.packageTypesData.filter(item => item.boxType.id === boxType.id)
            this.packageModel.packageType = null
            this.packageModel.packageTypeId = null
            this.packageModel.boxTypeId = boxType.id
        },
        changePackageType(packageType) {
            this.packageModel.packageTypeId = packageType.id
            this.packageModel.packageType = packageType
            this.packageModel.boxArrangement.z = packageType.length
            this.packageModel.boxArrangement.x = packageType.width
            this.packageModel.boxArrangement.y = packageType.height
        },
    }
}
</script>